import { useEffect, useSyncExternalStore } from 'react';

// Rozpoznawanie sklepu z lokalizacji: ustawienie (localStorage), uprawnienie przeglądarki i odczyt pozycji.
// Zasada: o zgodę pytamy wyłącznie po geście użytkownika (requestGeo). Odczyty w tle (getPosition)
// działają tylko przy włączonym ustawieniu i przyznanym uprawnieniu, więc nigdy nie pokażą pytania.

export interface GeoPosition {
  lat: number;
  lng: number;
  /** Dokładność w metrach (promień). */
  accM: number;
}

/** 'unknown' = przeglądarka bez Permissions API (starsze Safari) — stanu nie da się sprawdzić bez pytania. */
export type GeoPermission = 'unsupported' | 'unknown' | 'prompt' | 'granted' | 'denied';

const SETTING_KEY = 'odhaczka.geo';
/** Pozycja świeższa niż to nie jest odczytywana ponownie (wejście na ekran, powrót do aplikacji). */
export const POSITION_TTL_MS = 60_000;
/** Starsza pozycja jest zapominana po powrocie do aplikacji — telefon mógł już być gdzie indziej. */
const POSITION_STALE_MS = 5 * 60_000;
const API_TIMEOUT_MS = 15_000;
/** GeolocationPositionError.PERMISSION_DENIED (stała z liczbą — działa też z podstawionym geolocation w testach). */
const PERMISSION_DENIED = 1;

export const geoSupported = () => typeof navigator !== 'undefined' && 'geolocation' in navigator;

const listeners = new Set<() => void>();
function notify() {
  snapshot = null;
  for (const fn of listeners) fn();
}
function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

// --- Ustawienie ---

export function geoEnabled(): boolean {
  try {
    return localStorage.getItem(SETTING_KEY) === '1';
  } catch {
    return false;
  }
}

export function setGeoEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(SETTING_KEY, '1');
    else localStorage.removeItem(SETTING_KEY);
  } catch {
    // tryb prywatny albo zablokowane dane strony — ustawienie zostaje tylko w tej sesji
  }
  if (!on) cached = null;
  notify();
}

// --- Uprawnienie ---

let permission: GeoPermission | undefined;
let permissionStatus: PermissionStatus | null = null;
/** Udany odczyt po geście w tej sesji — jedyny dowód zgody w przeglądarkach bez Permissions API. */
let sessionGranted = false;

function setPermission(next: GeoPermission) {
  if (permission === next) return;
  permission = next;
  if (next === 'denied') cached = null;
  notify();
}

/** Stan uprawnienia do lokalizacji (bez pytania użytkownika). */
export async function geoPermission(): Promise<GeoPermission> {
  if (!geoSupported()) return 'unsupported';
  if (permissionStatus) return permissionStatus.state;
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    permissionStatus = status;
    // Zgodę można zmienić w ustawieniach przeglądarki w trakcie działania aplikacji.
    status.addEventListener('change', () => setPermission(status.state));
    setPermission(status.state);
    return status.state;
  } catch {
    setPermission('unknown');
    return 'unknown';
  }
}

const allowed = (p: GeoPermission | undefined) => p === 'granted' || (p === 'unknown' && sessionGranted);

/** Czy wolno czytać pozycję w tle: ustawienie włączone i zgoda przyznana. */
export async function geoActive(): Promise<boolean> {
  return geoEnabled() && allowed(await geoPermission());
}

// --- Pozycja ---

let cached: { pos: GeoPosition; atMs: number } | null = null;
let inflight: Promise<GeoPosition | null> | null = null;

interface ReadOptions {
  highAccuracy?: boolean;
  timeoutMs?: number;
  /** Akceptowany wiek pozycji (z pamięci aplikacji albo przeglądarki); 0 = świeży odczyt. */
  maximumAgeMs?: number;
}

function read({ highAccuracy = false, timeoutMs = 10_000, maximumAgeMs = 0 }: ReadOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: maximumAgeMs }),
  );
}

function remember(p: GeolocationPosition): GeoPosition {
  const pos = { lat: p.coords.latitude, lng: p.coords.longitude, accM: p.coords.accuracy };
  cached = { pos, atMs: Date.now() };
  notify();
  return pos;
}

/**
 * Bieżąca pozycja albo null (ustawienie wyłączone, brak zgody, brak sygnału, po czasie).
 * Nigdy nie pyta o zgodę. Równoległe wywołania czekają na jeden odczyt.
 */
export async function getPosition(opts: ReadOptions = {}): Promise<GeoPosition | null> {
  if (!(await geoActive())) return null;
  if (cached && Date.now() - cached.atMs <= (opts.maximumAgeMs ?? 0)) return cached.pos;
  inflight ??= read(opts)
    .then(remember)
    .catch((e: GeolocationPositionError) => {
      // Odmowa przy przyznanym uprawnieniu = np. wyłączona lokalizacja w telefonie; stan rozstrzyga przeglądarka.
      if (e.code === PERMISSION_DENIED) setPermission(permissionStatus?.state ?? 'denied');
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export type GeoRequest = { status: 'granted'; position: GeoPosition | null } | { status: 'denied' } | { status: 'unsupported' };

/**
 * Prośba o zgodę i odczyt pozycji — wołać WYŁĄCZNIE z gestu użytkownika (przełącznik, „Adres stąd”).
 * 'granted' z position: null = zgoda jest, ale położenia nie udało się ustalić (brak sygnału, po czasie).
 */
export async function requestGeo(opts: ReadOptions = { highAccuracy: true, timeoutMs: 15_000 }): Promise<GeoRequest> {
  if (!geoSupported()) return { status: 'unsupported' };
  try {
    const pos = remember(await read(opts));
    sessionGranted = true;
    void geoPermission();
    notify();
    return { status: 'granted', position: pos };
  } catch (e) {
    const denied = (e as GeolocationPositionError).code === PERMISSION_DENIED;
    // Rozstrzyga stan uprawnienia: odmowa przy przyznanej zgodzie to np. wyłączona lokalizacja w telefonie,
    // a zamknięte pytanie bez decyzji przeglądarki zgłaszają różnie.
    const p = await geoPermission();
    if (p === 'granted' || (p === 'unknown' && !denied)) {
      if (p === 'unknown') sessionGranted = true;
      return { status: 'granted', position: null };
    }
    if (p === 'unknown') setPermission('denied');
    return { status: 'denied' };
  }
}

// --- Hooki ---

export interface GeoStatus {
  supported: boolean;
  enabled: boolean;
  /** undefined = jeszcze sprawdzamy. */
  permission: GeoPermission | undefined;
  /** Ustawienie włączone i zgoda przyznana — aplikacja może sama czytać pozycję. */
  active: boolean;
}

let snapshot: GeoStatus | null = null;
function statusSnapshot(): GeoStatus {
  snapshot ??= {
    supported: geoSupported(),
    enabled: geoEnabled(),
    permission: geoSupported() ? permission : 'unsupported',
    active: geoEnabled() && allowed(permission),
  };
  return snapshot;
}

/** Ustawienie i uprawnienie, odświeżane po zmianie (także w ustawieniach przeglądarki). */
export function useGeoStatus(): GeoStatus {
  const value = useSyncExternalStore(subscribe, statusSnapshot);
  useEffect(() => {
    void geoPermission();
  }, []);
  return value;
}

const cachedPosition = () => cached?.pos ?? null;

/**
 * Pozycja telefonu do podpowiedzi (null = nieznana albo lokalizacja wyłączona).
 * Odczyt przy wejściu na ekran i po powrocie do aplikacji, najwyżej raz na POSITION_TTL_MS.
 */
export function usePosition(): GeoPosition | null {
  const { active } = useGeoStatus();
  const pos = useSyncExternalStore(subscribe, cachedPosition);
  useEffect(() => {
    if (!active) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (cached && Date.now() - cached.atMs > POSITION_STALE_MS) {
        cached = null;
        notify();
      }
      void getPosition({ highAccuracy: true, timeoutMs: 10_000, maximumAgeMs: POSITION_TTL_MS });
    };
    refresh();
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [active]);
  return active ? pos : null;
}

// --- Geokodowanie przez serwer (Nominatim, dane © OpenStreetMap) ---

async function geoApi<T>(path: string): Promise<T | null> {
  if (!navigator.onLine) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), API_TIMEOUT_MS);
  try {
    const res = await fetch(path, { credentials: 'same-origin', signal: ctrl.signal });
    if (!res.ok) return null;
    return ((await res.json()) as { result: T | null }).result ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Współrzędne adresu (null = nie znaleziono, offline albo błąd usługi). */
export const geoSearch = (q: string) => geoApi<{ lat: number; lng: number; label: string }>(`/api/geo/search?q=${encodeURIComponent(q)}`);

/** Adres w miejscu (null = nie znaleziono, offline albo błąd usługi). */
export const geoReverse = (lat: number, lng: number) =>
  geoApi<{ label: string; address: string }>(`/api/geo/reverse?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}`);

// --- Prezentacja ---

/** „350 m”, „2,4 km”, „12 km”. */
export function formatDistance(m: number): string {
  const tens = Math.round(m / 10) * 10;
  if (tens < 1000) return `${Math.max(10, tens)} m`;
  const km = Math.round(m / 100) / 10;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
}

/** Link do mapy OpenStreetMap w danym miejscu. */
export const osmUrl = (lat: number, lng: number) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;
